import { ResolverContext } from '../types';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { trelloFetch } from '@trello/fetch';
import {
  QueryBoardAccessRequestArgs,
  QueryBoardAccessVerificationRequestArgs,
} from '../generated';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { getNetworkClient } from '../getNetworkClient';
import { token } from '@trello/session-cookie';
import { Analytics } from '@trello/atlassian-analytics';

export const boardAccessRequestResolver = async (
  _parent: unknown,
  args: QueryBoardAccessRequestArgs | null,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const { modelType, id } = args || {};
  const apiUrl = `/1/${modelType}/${id}/requestAccess`;
  const response = await trelloFetch(
    apiUrl,
    {
      credentials: 'same-origin',
    },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'RequestAccess.boardAccessRequest',
        operationName: context.operationName,
      },
    },
  );

  const statusCode = response.status;

  const result = {
    allowed: true,
    reason: '',
  };
  if (!response.ok) {
    if ([400, 429].includes(statusCode)) {
      const res = await response.json();
      result.allowed = false;
      result.reason = res.message;
    } else {
      throw await parseNetworkError(response);
    }
  }

  return prepareDataForApolloCache(result, info);
};

export const sendBoardAccessRequest = async (
  obj: object,
  args: QueryBoardAccessRequestArgs | null,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const { modelType, id, traceId } = args || {};
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/${modelType}/${id}/requestAccess`);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };
  if (traceId) {
    headers['X-Trello-TraceId'] = traceId;
  }

  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, info);
};

export const boardAccessVerificationRequestResolver = async (
  _parent: unknown,
  args: QueryBoardAccessVerificationRequestArgs | null,
  context: ResolverContext,
  info: QueryInfo,
) => {
  const { idBoard, idInvitedMember } = args || {};
  const apiUrl = `/1/board/${idBoard}/accessRequests/${idInvitedMember}`;
  const response = await trelloFetch(
    apiUrl,
    {
      credentials: 'same-origin',
    },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'RequestAccess.boardAccessRequest',
        operationName: context.operationName,
      },
    },
  );

  const result = {
    success: true,
  };
  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache(result, info);
};
